import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

/**
 * A one-page newsletter issue.
 *
 * Issues are a structured document rather than prose — editor's note, pro tips,
 * a highlight, articles, a toolbox, resources, a deep dive, advisories — so
 * this renders named sections instead of a body. Every section is optional:
 * the two imported issues do not carry the same set, and future ones will not
 * either, so anything missing is simply skipped rather than rendered empty.
 *
 * Nothing here uses dangerouslySetInnerHTML. The content is a stored JSON
 * document, and everything in it reaches the page as a React text node.
 */

function Section({ title, children }) {
  if (!children) return null;
  return (
    <section className="nl-section">
      {title && <h2 className="nl-head">{title}</h2>}
      {children}
    </section>
  );
}

/** Renders paragraphs from a string with blank-line breaks. */
function Prose({ text }) {
  if (!text) return null;
  return String(text).split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>);
}

export default function Newsletter() {
  const { slug } = useParams();
  const [issue, setIssue] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    setState('loading');
    fetch(`/api/newsletters/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('missing'))))
      .then((d) => { setIssue(d.issue); setState('ready'); })
      .catch(() => setState('missing'));
  }, [slug]);

  useEffect(() => {
    if (issue) document.title = `${issue.title} | Ennoveda`;
    return () => { document.title = 'Ennoveda Inc. — Brilliance at the Basics'; };
  }, [issue]);

  if (state === 'loading') {
    return <section className="section"><div className="shell"><p className="dim mono">Loading…</p></div></section>;
  }

  if (state === 'missing') {
    return (
      <section className="section">
        <div className="shell">
          <h1>Not found</h1>
          <p className="lede" style={{ marginTop: '1rem' }}>That issue isn't here.</p>
          <p style={{ marginTop: '1.6rem' }}>
            <Link className="btn btn-ghost" to="/blog">Back to the Cyber Brief</Link>
          </p>
        </div>
      </section>
    );
  }

  const b = issue.body || {};
  const note = b.editor_note;

  return (
    <article className="newsletter">
      <section className="page-head">
        <div className="shell" style={{ maxWidth: '48rem' }}>
          <p className="eyebrow">
            {b.masthead_word || 'Newsletter'}
            {b.edition ? ` · ${b.edition}` : ''}
          </p>
          <h1>{issue.title}</h1>
          {issue.summary && <p className="lede" style={{ marginTop: '1.2rem' }}>{issue.summary}</p>}
          <p className="faint mono" style={{ marginTop: '1.2rem', fontSize: '0.8rem' }}>
            {issue.issue_date}
          </p>
        </div>
      </section>

      {issue.hero_image_url && (
        <div className="shell" style={{ maxWidth: '48rem' }}>
          <img className="post-hero" src={issue.hero_image_url} alt="" />
        </div>
      )}

      <section className="section" style={{ paddingTop: '2.5rem' }}>
        <div className="shell nl-shell">

          {note && (
            <div className="railed nl-note">
              {note.title && <h2 className="nl-note-title">{note.title}</h2>}
              {note.subtitle && <p className="nl-note-sub">{note.subtitle}</p>}
              <div className="prose"><Prose text={note.content} /></div>
            </div>
          )}

          <Section title="Pro tips">
            {Array.isArray(b.protips) && b.protips.length ? (
              <div className="grid grid-3">
                {b.protips.map((t, i) => (
                  <div className="card" key={i}>
                    <h3>{t.title}</h3>
                    <p className="dim" style={{ marginTop: '0.5rem', fontSize: '0.94rem' }}>{t.content}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </Section>

          {b.highlight && (
            <div className="nl-highlight">
              <h3>{b.highlight.title}</h3>
              <p>{b.highlight.content}</p>
            </div>
          )}

          <Section>
            {Array.isArray(b.articles) && b.articles.length ? (
              <div className="prose nl-articles">
                {b.articles.map((a, i) => (
                  <div key={i}>
                    <h2>{a.heading}</h2>
                    <Prose text={a.body} />
                  </div>
                ))}
              </div>
            ) : null}
          </Section>

          {b.deep_dive && (
            <Section title={b.deep_dive.title}>
              <div className="prose"><Prose text={b.deep_dive.body} /></div>
            </Section>
          )}

          {b.toolbox && Array.isArray(b.toolbox.items) && (
            <Section title={b.toolbox.title || 'Toolbox'}>
              <ul className="nl-list">
                {b.toolbox.items.map((it, i) => (
                  <li key={i}>
                    <strong>{it.lead}</strong> <span className="dim">{it.text}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {b.advisories && Array.isArray(b.advisories.items) && (
            <Section title={b.advisories.title || 'Advisories'}>
              <ul className="nl-advisories">
                {b.advisories.items.map((a, i) => (
                  <li key={i}>
                    <span className="tag tag-red">{a.cve}</span>
                    <div>
                      <h4>{a.name}</h4>
                      <p className="dim">{a.action}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {b.resources && Array.isArray(b.resources.links) && (
            <Section title={b.resources.title || 'Resources'}>
              {b.resources.intro && <p className="dim">{b.resources.intro}</p>}
              <ul className="nl-list">
                {b.resources.links.map((l, i) => (
                  <li key={i}>
                    {/^https?:\/\//i.test(l.url || '')
                      ? <a href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a>
                      : <span>{l.label}</span>}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {b.footer_tagline && (
            <p className="nl-tagline">{b.footer_tagline}</p>
          )}

          <p style={{ marginTop: '3rem' }}>
            <Link className="btn btn-ghost" to="/blog">All issues</Link>
          </p>
        </div>
      </section>
    </article>
  );
}
